"use client";
import { SignIn, useSignIn, useUser } from "@clerk/nextjs";
import { Button } from "@repo/design-system/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@repo/design-system/components/ui/card";
import { Input } from "@repo/design-system/components/ui/input";
import { Label } from "@repo/design-system/components/ui/label";
import { Loader } from "lucide-react";
import { useRouter } from "next/navigation";
import type React from "react";
import { useEffect, useId, useState } from "react";

type RedirectSignInStrategy = "enterprise_sso" | "oauth_google";

const USE_CLERK_SIGN_IN_COMPONENT = false;
const POST_SIGN_IN_FALLBACK_REDIRECT_URL = "/home";

const PERNOD_RICARD_ENTERPRISE_CONNECTION_ID =
	"samlc_3Dwt1otkTJlqMHV4FYEwVun5mZf";
const PERNOD_RICARD_CONNECTION_DISCOVERY_EMAIL =
	"sso-touchstack@pernod-ricard.com";

const ClerkSignInForm = () => (
	<div data-testid="login-form">
		<SignIn
			path="/sign-in"
			routing="path"
			signUpUrl="/sign-up"
			fallbackRedirectUrl={POST_SIGN_IN_FALLBACK_REDIRECT_URL}
			oauthFlow="redirect"
		/>
	</div>
);

const CustomLoginForm = () => {
	const router = useRouter();
	const { isSignedIn, isLoaded } = useUser();
	const { signIn, setActive } = useSignIn();
	const emailId = useId();
	const passwordId = useId();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [redirectLoading, setRedirectLoading] =
		useState<RedirectSignInStrategy | null>(null);

	function isSafeInternalPath(path: string) {
		try {
			if (!path.startsWith("/")) {
				return false;
			}
			const resolved = new URL(path, window.location.origin);
			return resolved.origin === window.location.origin;
		} catch {
			return false;
		}
	}

	function getPostSignInRedirect(params: URLSearchParams) {
		const redirectUrl = params.get("redirect_url");

		if (redirectUrl && isSafeInternalPath(redirectUrl)) {
			return redirectUrl;
		}

		return POST_SIGN_IN_FALLBACK_REDIRECT_URL;
	}
	// biome-ignore lint/correctness/useExhaustiveDependencies: This is a false positive
	useEffect(() => {
		if (isSignedIn && isLoaded) {
			const urlParams = new URLSearchParams(window.location.search);
			router.push(getPostSignInRedirect(urlParams));
		}
	}, [isLoaded]);

	async function onSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!signIn) {
			return;
		}

		setError(null);
		setIsLoading(true);

		try {
			const urlParams = new URLSearchParams(window.location.search);
			const attempt = await signIn.create({
				identifier: email,
				password,
				actionCompleteRedirectUrl: `${urlParams.get("redirect_url")}`,
			});
			if (attempt.status === "complete") {
				await setActive({
					session: attempt.createdSessionId,
					redirectUrl: `${urlParams.get("redirect_url")}`,
				});
			}
		} catch (err) {
			if (
				(err as { errors?: { message: string; code: string }[] })?.errors?.[0]
					?.code === "session_exists"
			) {
				const urlParams = new URLSearchParams(window.location.search);
				router.push(getPostSignInRedirect(urlParams));
			} else {
				setError(
					(err as { errors?: { message: string }[] })?.errors?.[0]?.message ||
						"Something went wrong. Please try again.",
				);
			}
		} finally {
			setIsLoading(false);
		}
	}

	async function signInWithRedirect(strategy: RedirectSignInStrategy) {
		if (!signIn) {
			return;
		}
		setError(null);
		setRedirectLoading(strategy);
		try {
			await signIn.authenticateWithRedirect({
				strategy,
				redirectUrl: "/sign-in/sso-callback",
				redirectUrlComplete: "/campaigns",
			});
		} catch (err) {
			setError(
				(err as { errors?: { message: string }[] })?.errors?.[0]?.message ||
					"SSO sign-in failed.",
			);
		} finally {
			setRedirectLoading(null);
		}
	}

	async function signInWithPernodRicard() {
		if (!signIn) {
			return;
		}

		setError(null);
		setRedirectLoading("enterprise_sso");
		try {
			const urlParams = new URLSearchParams(window.location.search);
			const attempt = await signIn.create({
				identifier: PERNOD_RICARD_CONNECTION_DISCOVERY_EMAIL,
			});
			const preparedAttempt = await attempt.prepareFirstFactor({
				strategy: "enterprise_sso",
				redirectUrl: "/sign-in/sso-callback",
				actionCompleteRedirectUrl: getPostSignInRedirect(urlParams),
				enterpriseConnectionId: PERNOD_RICARD_ENTERPRISE_CONNECTION_ID,
			});
			const externalRedirectUrl =
				preparedAttempt.firstFactorVerification.externalVerificationRedirectURL;

			if (!externalRedirectUrl) {
				throw new Error("Missing Pernod Ricard SSO redirect URL.");
			}

			const redirectUrl = new URL(externalRedirectUrl.toString());
			redirectUrl.searchParams.delete("login_hint");
			window.location.assign(redirectUrl.toString());
		} catch (err) {
			setError(
				(err as { errors?: { message: string }[] })?.errors?.[0]?.message ||
					"Pernod Ricard SSO sign-in failed.",
			);
			setRedirectLoading(null);
		}
	}

	return isLoaded ? (
		<Card>
			<CardHeader className="text-center">
				<CardTitle className="text-xl">Welcome back</CardTitle>
				<CardDescription>Login with SSO or your Google account</CardDescription>
			</CardHeader>
			<CardContent>
				<form onSubmit={onSubmit}>
					<div className="grid gap-6">
						<div className="flex flex-col gap-4">
							<Button
								variant="outline"
								className="w-full"
								onClick={signInWithPernodRicard}
								type="button"
								disabled={redirectLoading === "enterprise_sso"}
								title="Pernod Ricard SSO via Clerk SAML connection"
							>
								{redirectLoading === "enterprise_sso" && (
									<Loader className="mr-2 h-4 w-4 animate-spin" />
								)}
								Login with Pernod Ricard
							</Button>
							<Button
								variant="outline"
								className="w-full"
								onClick={() => signInWithRedirect("oauth_google")}
								type="button"
								disabled={redirectLoading === "oauth_google"}
							>
								{redirectLoading === "oauth_google" ? (
									<Loader className="mr-2 h-4 w-4 animate-spin" />
								) : (
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 24 24"
										className="mr-2 h-4 w-4"
									>
										<title>Google logo</title>
										<path
											d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
											fill="currentColor"
										/>
									</svg>
								)}
								Login with Google
							</Button>
						</div>
						<div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-border after:border-t">
							<span className="relative z-10 bg-background px-2 text-muted-foreground">
								Or continue with
							</span>
						</div>
						<div className="grid gap-6">
							<div className="grid gap-2">
								<Label htmlFor={emailId}>Email</Label>
								<Input
									id={emailId}
									type="email"
									placeholder="m@example.com"
									required
									value={email}
									onChange={(e) => setEmail(e.target.value)}
								/>
							</div>
							<div className="grid gap-2">
								<Label htmlFor={passwordId}>Password</Label>
								<Input
									id={passwordId}
									type="password"
									required
									value={password}
									onChange={(e) => setPassword(e.target.value)}
								/>
							</div>
							<Button type="submit" className="w-full" disabled={isLoading}>
								{isLoading ? (
									<>
										<Loader className="mr-2 h-4 w-4 animate-spin" />
										Logging in...
									</>
								) : (
									"Login"
								)}
							</Button>

							{error && (
								<p className="text-center text-red-500 text-sm">{error}</p>
							)}
						</div>
						{/* <div className="text-center text-sm">
                Don&apos;t have an account?{' '}
								<Link href="/sign-up" className="underline underline-offset-4">
									Sign up
								</Link>
							</div> */}
					</div>
				</form>
			</CardContent>
		</Card>
	) : (
		<div className="flex w-full justify-center gap-6">
			<Loader className="h-[48px] w-[48px] animate-spin" />
		</div>
	);
};

const LoginForm = () => {
	if (USE_CLERK_SIGN_IN_COMPONENT) {
		return <ClerkSignInForm />;
	}

	return <CustomLoginForm />;
};

export default LoginForm;
